const SECTION_KEY_OVERRIDES = { hr: "hr.area" };
const ROUTE_KEY_OVERRIDES = {
    Articolo: "articles.detail",
    CostiArticolo: "articles.costs.view",
};

function keyPart(value) {
    return String(value || "")
        .normalize("NFD")
        .replace(/[\u0300-\u036f]/g, "")
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, "-")
        .replace(/^-+|-+$/g, "") || "pagina";
}

function titleFromKey(value) {
    return String(value || "Dashboard")
        .replace(/[-_]+/g, " ")
        .replace(/\b\w/g, (letter) => letter.toUpperCase());
}

function sectionIdForRoute(route) {
    if (route.meta?.menu) return route.meta.menu;
    const segment = String(route.path || "").split("/").filter(Boolean)[0];
    return segment ? keyPart(segment.replace(/:.+$/, "")) : "home";
}

function sectionKey(sectionId) {
    return SECTION_KEY_OVERRIDES[sectionId] || `section.${keyPart(sectionId)}`;
}

function routeKey(route) {
    return route.meta?.access?.key || ROUTE_KEY_OVERRIDES[route.name] || `page.${keyPart(route.name || route.path)}`;
}

function menuItems(menu) {
    const rows = [];
    const visit = (items) => {
        (items || []).forEach((item) => {
            rows.push(item);
            visit(item.submenu);
        });
    };
    visit(menu?.submenu);
    return rows;
}

function menuItemByRouteName(menu, routeName) {
    return menuItems(menu).find((item) => item.route?.name === routeName);
}

function uniqueGroups(groups) {
    return Array.from(new Set((groups || []).filter((group) => typeof group === "string" && group.trim())));
}

function catalogDefaults(groups, adminOnly = false) {
    const defaultGroups = uniqueGroups(groups);
    return {
        default_access_mode: adminOnly || defaultGroups.length ? "restricted" : "authenticated",
        default_groups: defaultGroups,
    };
}

function catalogRoutes(routes) {
    return (routes || []).flatMap((route) => [route, ...catalogRoutes(route.children || [])]);
}

function isCatalogRoute(route) {
    return Boolean(
        route &&
        route.component &&
        route.path !== "*" &&
        route.path !== "/login" &&
        !route.meta?.publicAuth
    );
}

function assertUniqueCatalogKeys(items) {
    const seen = new Set();
    const duplicates = new Set();
    items.forEach((item) => {
        if (seen.has(item.key)) duplicates.add(item.key);
        seen.add(item.key);
    });
    if (duplicates.size) {
        throw new Error(`Chiavi accesso duplicate: ${Array.from(duplicates).join(", ")}`);
    }
}

export function buildAccessCatalog(routes, menus) {
    const items = [];
    const sections = new Map();
    const pageKeysByRouteName = new Map();
    const cataloguedRoutes = catalogRoutes(routes).filter(isCatalogRoute);

    cataloguedRoutes.forEach((route, routeIndex) => {
        // A sibling view can share an existing capability without registering
        // another page or weakening the route guard's current capability check.
        if (route.meta?.access?.inheritFrom) {
            pageKeysByRouteName.set(route.name, route.meta.access.inheritFrom);
            return;
        }
        const sectionId = sectionIdForRoute(route);
        const menu = menus[sectionId];
        const parentKey = sectionKey(sectionId);
        if (!sections.has(parentKey)) {
            sections.set(parentKey, {
                key: parentKey,
                name: menu?.title || titleFromKey(sectionId),
                kind: "section",
                parent_key: "",
                route_name: "",
                route_path: route.path || "",
                description: `Sezione ${menu?.title || titleFromKey(sectionId)} della Dashboard.`,
                sort_order: routeIndex * 100,
                ...catalogDefaults(menu?.groups, menu?.admin === true),
            });
        }

        const menuItem = menuItemByRouteName(menu, route.name);
        const key = routeKey(route);
        const page = {
            key,
            name: route.meta?.access?.name || route.meta?.title || menuItem?.title || titleFromKey(route.name),
            kind: "page",
            parent_key: parentKey,
            route_name: route.name || "",
            route_path: route.path || "",
            description: route.meta?.access?.description || `Accesso alla pagina ${route.meta?.title || route.name}.`,
            sort_order: routeIndex * 100 + 10,
            ...catalogDefaults(
                route.meta?.access?.defaultGroups || menuItem?.groups,
                route.meta?.access?.admin === true || menuItem?.admin === true,
            ),
        };
        items.push(page);
        if (route.name) pageKeysByRouteName.set(route.name, key);

        (route.meta?.accessModules || []).forEach((module, moduleIndex) => {
            items.push({
                key: module.key,
                name: module.name,
                kind: module.kind || "component",
                parent_key: key,
                route_name: route.name || "",
                route_path: route.path || "",
                description: module.description || "Modulo configurabile della pagina.",
                sort_order: routeIndex * 100 + 20 + moduleIndex,
                ...catalogDefaults(
                    module.defaultGroups,
                    module.admin === true || module.defaultAccessMode === "restricted",
                ),
            });
        });
    });

    const orderedSections = Array.from(sections.values());
    const catalogItems = [...orderedSections, ...items];
    assertUniqueCatalogKeys(catalogItems);
    for (const key of pageKeysByRouteName.values()) {
        if (!catalogItems.some(item => item.key === key)) throw new Error(`Capability ereditata non definita: ${key}`);
    }
    return {
        items: catalogItems,
        pageKeysByRouteName,
    };
}

export function bindAccessCatalog(routes, menus, catalog) {
    catalogRoutes(routes).filter(isCatalogRoute).forEach((route) => {
        const key = catalog.pageKeysByRouteName.get(route.name) || routeKey(route);
        route.meta = { ...(route.meta || {}), accessCapability: key };
    });

    Object.entries(menus).forEach(([sectionId, menu]) => {
        menu.capability = menu.capability || sectionKey(sectionId);
        menuItems(menu).forEach((item) => {
            if (item.route?.name && catalog.pageKeysByRouteName.has(item.route.name)) {
                item.capability = catalog.pageKeysByRouteName.get(item.route.name);
            }
        });
    });
}
